import React, { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldAlert, Power, PlayCircle } from "lucide-react";

export default function DevControl() {
  const [key, setKey] = useState(() => localStorage.getItem("bc_dev_key") || "");
  const [suspended, setSuspended] = useState(null); // null unknown
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [fee, setFee] = useState("");
  const [feeSaving, setFeeSaving] = useState(false);
  const [feeMsg, setFeeMsg] = useState("");

  const loadFee = async (k) => {
    try {
      const { data } = await api.get("/dev/ordering-fee", { params: { key: k } });
      setFee(data.fee.toFixed(2));
    } catch (e) { /* ignore */ }
  };

  const check = async () => {
    setLoading(true); setMsg("");
    try {
      const { data } = await api.get("/dev/kill-switch", { params: { key } });
      setSuspended(data.suspended);
      localStorage.setItem("bc_dev_key", key);
      loadFee(key);
    } catch (e) {
      setMsg(formatApiError(e.response?.data?.detail) || "Invalid key");
      setSuspended(null);
    } finally { setLoading(false); }
  };

  const saveFee = async () => {
    setFeeSaving(true); setFeeMsg("");
    try {
      const { data } = await api.post("/dev/ordering-fee", { key, fee: parseFloat(fee) });
      setFee(data.fee.toFixed(2));
      setFeeMsg(`Saved. Ordering fee is now £${data.fee.toFixed(2)} per order.`);
    } catch (e) {
      setFeeMsg(formatApiError(e.response?.data?.detail) || "Failed to save fee");
    } finally { setFeeSaving(false); }
  };

  const setState = async (suspend) => {
    setLoading(true); setMsg("");
    try {
      const { data } = await api.post("/dev/kill-switch", { key, suspend });
      setSuspended(data.suspended);
      localStorage.setItem("bc_dev_key", key);
      setMsg(suspend ? "Ordering is now SUSPENDED for customers." : "Ordering is now LIVE.");
    } catch (e) {
      setMsg(formatApiError(e.response?.data?.detail) || "Invalid key");
    } finally { setLoading(false); }
  };

  const clearOrders = async () => {
    if (!window.confirm("Delete ALL orders and payment records? This wipes the live orders board and history. Cannot be undone.")) return;
    setLoading(true); setMsg("");
    try {
      const { data } = await api.post("/dev/clear-orders", { key });
      setMsg(`Cleared ${data.deleted_orders} orders. Next order will be #1042.`);
    } catch (e) {
      setMsg(formatApiError(e.response?.data?.detail) || "Failed to clear orders");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-brand-dark grain flex items-center justify-center px-6">
      <div className="w-full max-w-sm bg-brand-cream rounded-3xl p-6 shadow-2xl" data-testid="dev-control">
        <div className="flex items-center gap-2 mb-1">
          <ShieldAlert className="w-6 h-6 text-brand-teal" />
          <h1 className="font-head text-2xl font-bold text-brand-dark">Service control</h1>
        </div>
        <p className="text-sm text-brand-dark/50 mb-5">Developer only. Suspends online ordering for all customers.</p>

        <Label className="text-xs">Developer key</Label>
        <div className="flex gap-2 mt-1 mb-4">
          <Input data-testid="dev-key-input" type="password" value={key} onChange={(e) => setKey(e.target.value)}
            placeholder="Enter key" className="h-11 rounded-xl bg-white" onKeyDown={(e) => e.key === "Enter" && check()} />
          <Button data-testid="dev-check-btn" onClick={check} disabled={loading || !key} className="h-11 rounded-xl bg-brand-dark text-white">Check</Button>
        </div>

        {suspended !== null && (
          <>
            <div className={`rounded-2xl p-4 mb-4 text-center font-semibold ${suspended ? "bg-red-50 text-red-700 border border-red-200" : "bg-brand-teal/10 text-brand-teal-dark border border-brand-teal/30"}`} data-testid="dev-status">
              Ordering is currently {suspended ? "SUSPENDED" : "LIVE"}
            </div>
            {suspended ? (
              <Button data-testid="dev-resume-btn" onClick={() => setState(false)} disabled={loading}
                className="w-full h-14 rounded-2xl bg-brand-teal hover:bg-brand-teal-dark text-white font-bold">
                <PlayCircle className="w-5 h-5 mr-2" /> Resume ordering
              </Button>
            ) : (
              <Button data-testid="dev-suspend-btn" onClick={() => setState(true)} disabled={loading}
                className="w-full h-14 rounded-2xl bg-red-600 hover:bg-red-700 text-white font-bold">
                <Power className="w-5 h-5 mr-2" /> Suspend ordering
              </Button>
            )}
          </>
        )}

        {suspended !== null && (
          <div className="mt-4 pt-4 border-t border-brand-dark/10">
            <Label className="text-xs">Online ordering fee (£ per order)</Label>
            <div className="flex gap-2 mt-1">
              <Input data-testid="dev-fee-input" type="number" step="0.01" min="0" max="5" value={fee}
                onChange={(e) => setFee(e.target.value)} placeholder="0.30"
                className="h-11 rounded-xl bg-white" onKeyDown={(e) => e.key === "Enter" && saveFee()} />
              <Button data-testid="dev-fee-save" onClick={saveFee} disabled={feeSaving || fee === ""}
                className="h-11 rounded-xl bg-brand-teal hover:bg-brand-teal-dark text-white font-semibold">Save fee</Button>
            </div>
            <p className="text-xs text-brand-dark/40 mt-1">Applies instantly to customer checkout and your billing — no redeploy needed.</p>
            {feeMsg && <p className="text-xs text-brand-dark/70 mt-2" data-testid="dev-fee-msg">{feeMsg}</p>}
          </div>
        )}

        {suspended !== null && (
          <div className="mt-4 pt-4 border-t border-brand-dark/10">
            <p className="text-xs text-brand-dark/40 mb-2">Danger zone — wipes all orders (test data) from this environment.</p>
            <Button data-testid="dev-clear-orders" onClick={clearOrders} disabled={loading} variant="outline"
              className="w-full h-11 rounded-2xl border-red-300 text-red-600 font-semibold hover:bg-red-50">
              Clear ALL orders
            </Button>
          </div>
        )}

        {msg && <p className="text-sm text-center text-brand-dark/70 mt-4" data-testid="dev-msg">{msg}</p>}
      </div>
    </div>
  );
}
